import React, { useState } from "react";
import api from '../../api';

const PvPChallengeReceivedModal = ({ challenge, onClose, onAccept, onDecline }) => {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  // Get theme colors from localStorage
  const getThemeColors = () => {
    const theme = localStorage.getItem("appTheme") || "default";
    
    const themeColors = {
      default: { primary: "#556ee6", secondary: "#2a3042" },
      ocean: { primary: "#17a2b8", secondary: "#1a2332" },
      forest: { primary: "#28a745", secondary: "#1e2d1e" },
      sunset: { primary: "#fd7e14", secondary: "#2d1e1e" },
      purple: { primary: "#6f42c1", secondary: "#2d1e2d" },
      crimson: { primary: "#dc3545", secondary: "#2d1e1e" }
    };
    
    return themeColors[theme] || themeColors.default;
  };

  const themeColors = getThemeColors();

  const handleAccept = async () => {
    setLoading(true);
    setErrorMessage(null);
    
    try {
      const response = await api.post(`/v1/pvp/challenge/${challenge.challenge_id}/accept`, {}, { withCredentials: true });
      
      if (response.data.success) {
        onAccept?.(response.data);
        onClose();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || "Failed to accept challenge!";
      setErrorMessage(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      setLoading(false);
    }
  };

  const handleDecline = async () => {
    setLoading(true);
    setErrorMessage(null);
    
    try {
      const response = await api.post(`/v1/pvp/challenge/${challenge.challenge_id}/decline`, {}, { withCredentials: true });
      
      if (response.data.success) {
        onDecline?.(response.data);
        onClose();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || "Failed to decline challenge!";
      setErrorMessage(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      setLoading(false);
    }
  };

  const getBattleTypeInfo = () => {
    switch (challenge.battle_type) {
      case 'casual':
        return { label: 'Casual Battle', desc: 'No betting, just for fun', icon: '/items/pokeball.png', color: '#10b981' };
      case 'coin_bet':
        return { label: 'Coin Battle', desc: 'Bet regular coins', icon: '/items/coin.png', color: '#f59e0b' };
      case 'battle_coin_bet':
        return { label: 'Battle Coin Battle', desc: 'Bet battle coins', icon: '/items/battle_coin.png', color: '#8b5cf6' };
      default:
        return { label: 'Unknown', desc: '', icon: '/items/pokeball.png', color: '#6b7280' };
    }
  };

  const battleTypeInfo = getBattleTypeInfo();

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px 10px',
      boxSizing: 'border-box',
      zIndex: 1050
    }} onClick={onClose}>
      <div 
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: '500px',
          maxHeight: 'calc(100vh - 40px)',
          background: 'rgba(29, 34, 46, 0.95)',
          color: '#fff',
          borderRadius: '12px',
          backdropFilter: 'blur(10px)',
          border: `1px solid ${themeColors.primary}40`,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px',
          borderBottom: `1px solid ${themeColors.primary}40`,
          background: 'rgba(0, 0, 0, 0.2)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <img
              src="/items/battleseek.png"
              alt="Battle Challenge"
              width={32}
              height={32}
              style={{ imageRendering: 'pixelated' }}
            />
            <div>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color: themeColors.primary }}>
                Battle Challenge Received!
              </h3>
              <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: 'rgba(255, 255, 255, 0.7)' }}>
                From {challenge.challenger_username}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'transparent',
              border: 'none',
              color: themeColors.primary,
              fontSize: '24px',
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: '4px'
            }}
            onMouseEnter={(e) => e.target.style.background = `${themeColors.primary}20`}
            onMouseLeave={(e) => e.target.style.background = 'transparent'}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{
          padding: '20px',
          overflowY: 'auto',
          flex: 1,
          background: 'rgba(0, 0, 0, 0.05)'
        }}>
          {errorMessage && (
            <div style={{
              background: 'rgba(220, 38, 38, 0.1)',
              border: '1px solid rgba(220, 38, 38, 0.3)',
              color: '#fca5a5',
              padding: '12px',
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: '14px'
            }}>
              {errorMessage}
            </div>
          )}

          {/* Battle Type */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            padding: '16px',
            border: `2px solid ${battleTypeInfo.color}40`,
            borderRadius: '12px',
            background: `${battleTypeInfo.color}10`,
            marginBottom: '16px'
          }}>
            <img 
              src={battleTypeInfo.icon} 
              alt={battleTypeInfo.label}
              style={{ width: '32px', height: '32px', marginRight: '16px', imageRendering: 'pixelated' }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 'bold', color: '#fff', fontSize: '16px' }}>{battleTypeInfo.label}</div>
              <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.7)', marginTop: '4px' }}>{battleTypeInfo.desc}</div>
            </div>
          </div>

          {/* Betting Details */}
          {challenge.battle_type !== 'casual' && challenge.bet_amount > 0 && (
            <div style={{
              padding: '16px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '12px',
              background: 'rgba(255, 255, 255, 0.05)',
              marginBottom: '16px'
            }}>
              <h4 style={{ color: themeColors.primary, marginBottom: '12px', fontSize: '16px' }}>
                Betting Details
              </h4>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <img 
                  src={`/items/${challenge.currency_type === 'coins' ? 'coin' : 'battle_coin'}.png`}
                  alt={challenge.currency_type}
                  style={{ width: '24px', height: '24px', imageRendering: 'pixelated' }}
                />
                <div>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#fff' }}>
                    {challenge.bet_amount.toLocaleString()} {challenge.currency_type === 'coins' ? 'Coins' : 'Battle Coins'}
                  </div>
                  <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)' }}>
                    Winner takes all ({(challenge.bet_amount * 2).toLocaleString()})
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Item Rules */}
          <div style={{
            padding: '16px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            background: 'rgba(255, 255, 255, 0.05)',
            marginBottom: '16px'
          }}>
            <h4 style={{ color: themeColors.primary, marginBottom: '12px', fontSize: '16px' }}>
            Item Rules
            </h4>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <img 
                src="/items/potion.png"
                alt="Items"
                style={{ width: '24px', height: '24px', imageRendering: 'pixelated' }}
              />
              <div>
                <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#fff' }}>
                  {challenge.items_allowed ? 'Items Allowed' : 'No Items Allowed'}
                </div>
                {challenge.items_allowed && challenge.max_items_per_player > 0 && (
                  <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)' }}>
                    Max {challenge.max_items_per_player} items per player
                  </div>
                )}
                {challenge.items_allowed && challenge.max_items_per_player === 0 && (
                  <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)' }}>
                    Unlimited items allowed
                  </div>
                )}
                {!challenge.items_allowed && (
                  <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)' }}>
                    Pure battle skills only
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Warning for betting battles */}
          {challenge.battle_type !== 'casual' && challenge.bet_amount > 0 && (
            <div style={{
              padding: '12px',
              background: 'rgba(251, 146, 60, 0.1)',
              border: '1px solid rgba(251, 146, 60, 0.3)',
              borderRadius: '8px',
              fontSize: '13px',
              color: '#fbbf24'
            }}>
              ⚠️ <strong>Warning:</strong> Your {challenge.bet_amount.toLocaleString()} {challenge.currency_type === 'coins' ? 'coins' : 'battle coins'} will be deducted immediately upon accepting this challenge.
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '20px',
          borderTop: `1px solid ${themeColors.primary}40`,
          background: 'rgba(0, 0, 0, 0.2)',
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={handleDecline}
            disabled={loading}
            style={{
              padding: '12px 24px',
              background: 'rgba(220, 38, 38, 0.2)',
              color: '#fca5a5',
              border: '1px solid rgba(220, 38, 38, 0.3)',
              borderRadius: '8px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
              transition: 'all 0.2s ease',
              opacity: loading ? 0.5 : 1
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.background = 'rgba(220, 38, 38, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.target.style.background = 'rgba(220, 38, 38, 0.2)';
              }
            }}
          >
            Decline
          </button>
          <button
            onClick={handleAccept}
            disabled={loading}
            style={{
              padding: '12px 24px',
              background: loading ? 'rgba(107, 114, 128, 0.5)' : themeColors.primary,
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = `0 4px 12px ${themeColors.primary}40`;
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }
            }}
          >
            <img 
              src="/items/battleseek.png" 
              alt="Accept" 
              style={{ width: '16px', height: '16px', imageRendering: 'pixelated' }}
            />
            {loading ? 'Accepting...' : 'Accept Challenge'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PvPChallengeReceivedModal;
